import { getFocusableElements } from '@components/modals';

/**
 * Accessibility wrapper for the third-party KiwiSizing size-guide app.
 *
 * The size guide is NOT theme markup: the trigger and the chart dialog are both
 * injected at runtime by the vendor script (on the PDP through a Shopify app
 * block, in quick-buy into our empty #KiwiSizingChart div). Neither can be fixed
 * in Liquid, so we upgrade the injected DOM after the fact.
 *
 * This is deliberately defensive: it rewrites third-party DOM and WILL break if
 * KiwiSizing changes its markup. Everything is feature-detected, nothing throws,
 * and the observer disconnects once it stops finding anything so it cannot run
 * for the lifetime of the page. The permanent fix is a vendor-side change.
 */

const TRIGGER_SELECTORS = [
  '#KiwiSizingChart [class*="kiwi"][class*="link"]',
  '#KiwiSizingChart a',
  '#KiwiSizingChart [onclick]',
  '#sizeChartContainer [class*="kiwi"][class*="link"]',
  '#sizeChartContainer a',
  '[class*="kiwi-sizing"] [class*="link"]',
];

const DIALOG_SELECTORS = ['[class*="kiwi"][class*="modal"]', '[class*="kiwi"][class*="popup"]', '#kiwi-sizing-modal'];

const UPGRADED = 'data-a11y-upgraded';
const MAX_IDLE_PASSES = 40;

/** Makes a non-semantic injected trigger keyboard operable. */
const upgradeTrigger = (el) => {
  if (!el || el.hasAttribute(UPGRADED)) return false;
  // Already semantic? Nothing to do.
  if (el.tagName === 'BUTTON' || (el.tagName === 'A' && el.hasAttribute('href'))) {
    el.setAttribute(UPGRADED, '');
    return false;
  }

  el.setAttribute(UPGRADED, '');
  el.setAttribute('role', 'button');
  if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
  el.setAttribute('aria-haspopup', 'dialog');

  el.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault(); // Space would otherwise scroll
    el.click();
  });

  return true;
};

/** Adds dialog semantics, a focus trap and Escape to the injected chart panel. */
const upgradeDialog = (el) => {
  if (!el || el.hasAttribute(UPGRADED)) return false;
  el.setAttribute(UPGRADED, '');

  if (!el.getAttribute('role')) el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');

  el.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      // Prefer the vendor's own close control so its internal state stays consistent.
      const closer = el.querySelector('[class*="close"], [aria-label*="close" i], [aria-label*="chiudi" i]');
      if (closer) closer.click();
      return;
    }

    if (e.key !== 'Tab') return;

    const focusable = getFocusableElements(el);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  return true;
};

const sweep = () => {
  let changed = false;

  TRIGGER_SELECTORS.forEach((selector) => {
    document.querySelectorAll(selector).forEach((el) => {
      if (upgradeTrigger(el)) changed = true;
    });
  });

  DIALOG_SELECTORS.forEach((selector) => {
    document.querySelectorAll(selector).forEach((el) => {
      if (upgradeDialog(el)) changed = true;
    });
  });

  return changed;
};

const initKiwiSizingA11y = () => {
  // Nothing to watch if the app is not on this page.
  if (!document.querySelector('#KiwiSizingChart, #sizeChartContainer, [class*="kiwi-sizing"]')) return;

  let idlePasses = 0;

  const observer = new MutationObserver(() => {
    try {
      idlePasses = sweep() ? 0 : idlePasses + 1;
      if (idlePasses > MAX_IDLE_PASSES) observer.disconnect();
    } catch {
      // Vendor DOM changed shape — stop rather than spam the console.
      observer.disconnect();
    }
  });

  try {
    sweep();
    observer.observe(document.body, { childList: true, subtree: true });
  } catch {
    observer.disconnect();
  }
};

export { initKiwiSizingA11y };
